import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { TokenService } from '@services/token.service';
import { CompaniesService } from '@services/companies.service';
import { SelfFilerService } from '@services/self-filer.service';
import { AppLayoutService } from '../../app-layout.service';
import { GoogleAnalyticsReportService } from '@services/google-analytics-report.service';
import { USA_COUNTRY_CODE } from '@constants/constants';
import { CompanyModel } from '@data-layer/models/companies';
import { enumUserBetaFlag } from '@app/data-layer/enums/user';

declare let Cookiebot: any;

@Component({
  selector: 'app-setting-sidebar',
  templateUrl: './setting-sidebar.component.html',
  styleUrls: ['./setting-sidebar.component.scss'],
})
export class SettingSidebarComponent implements OnInit, OnDestroy {
  public isOpen: boolean = false;
  public ACCOUNT_SETTINGS_URL: string = '/companies/account-settings/';
  public enumUserBetaFlag: typeof enumUserBetaFlag = enumUserBetaFlag;

  private subscription: Subscription[] = [];

  constructor(
    private router: Router,
    private appLayoutService: AppLayoutService,
    private tokenService: TokenService,
    private companiesService: CompaniesService,
    private selfFilerService: SelfFilerService,
    private googleAnalyticsReportService: GoogleAnalyticsReportService,
  ) {
    this.subscription.push(
      this.router.events.subscribe((val) => {
        if (val instanceof NavigationEnd && this.isOpen) {
          this.closeSidebar();
        }
      }),
    );
  }

  public ngOnInit(): void {
    this.subscription.push(
      this.appLayoutService.openSettingSidebar$.subscribe(() => {
        this.openSidebar();
      }),
    );
  }

  public ngOnDestroy(): void {
    this.subscription.forEach((e) => {
      e.unsubscribe();
    });
  }

  public get companies(): CompanyModel[] {
    return this.companiesService.getCompanies();
  }

  public get isShowOnBoarding(): boolean {
    return this.selfFilerService.isShowOnBoarding;
  }

  public get isShowSelfFiller(): boolean {
    const company: CompanyModel = this.companiesService.getCompany();

    return !!company && this.isShowOnBoarding && company?.address?.country?.code === USA_COUNTRY_CODE;
  }

  public closeSidebar(): void {
    this.isOpen = false;
  }

  public goToSecurity(): void {
    this.router.navigate(['companies', 'account-settings', 'security']);
    this.closeSidebar();
  }

  public goToUserDetails(): void {
    this.router.navigate(['companies', 'account-settings', 'user-details']);
    this.closeSidebar();
  }

  public goToOrganization(): void {
    this.router.navigate(['companies', 'account-settings', 'organization']);
    this.closeSidebar();
  }

  public goToBillingHistory(): void {
    this.router.navigate(['companies', 'account-settings', 'billing-history']);
    this.closeSidebar();
  }

  public goToGeneral(): void {
    this.router.navigate(['companies', 'account-settings', 'general']);
    this.closeSidebar();
  }

  public goToCompany(): void {
    const companyTabLink: string =
      this.companies.length > 1
        ? `${this.ACCOUNT_SETTINGS_URL}/company-list`
        : `${this.ACCOUNT_SETTINGS_URL}/company-view/${this.companiesService.getCompany().id}`;
    this.router.navigate([companyTabLink]);
    this.closeSidebar();
  }

  public goToLegalTerms(): void {
    this.router.navigate(['legal-terms']);
  }

  public goToPolicy(): void {
    this.router.navigate(['privacy-policy']);
  }

  public onOpenCookieSettings(): void {
    if (!!Cookiebot) {
      Cookiebot.renew();
    }
  }

  public logout(): void {
    this.tokenService.eraseToken();
    this.googleAnalyticsReportService.logout();
  }

  private openSidebar(): void {
    this.isOpen = true;
  }
}
